import { IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";

const emojis = ["🐘", "🐭", "🚀", "✈️", "🏠", "🛖", "🚌", "🚗", "🌳", "🌿", "🏰", "⛺", "🍎", "🍕", "🐶", "🐱", "⚽", "🏀", "🎈", "🎸"];

const LeftRightGame: React.FC = () => {
    const [leftEmoji, setLeftEmoji] = useState<string>("");
    const [rightEmoji, setRightEmoji] = useState<string>("");
    const [questionSide, setQuestionSide] = useState<string>(""); // "left" or "right"
    const [correctEmoji, setCorrectEmoji] = useState<string>("");
    const [message, setMessage] = useState("");
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    useEffect(() => {
        generateQuestion();
        document.title = "Left and Right Worksheets for UKG | Pre-Math Concept Activities";
    }, []);

    const generateQuestion = () => {
        const randomEmojis = [...emojis];

        // Pick two different random emojis
        const emoji1 = randomEmojis.splice(Math.floor(Math.random() * randomEmojis.length), 1)[0];
        const emoji2 = randomEmojis.splice(Math.floor(Math.random() * randomEmojis.length), 1)[0];

        // Randomly decide which side the question should ask for
        const isAskingLeft = Math.random() < 0.5;

        setLeftEmoji(emoji1);
        setRightEmoji(emoji2);
        setQuestionSide(isAskingLeft ? "left" : "right");
        setCorrectEmoji(isAskingLeft ? emoji1 : emoji2);
        setMessage("");
    };

    const checkAnswer = (selectedEmoji: string) => {
        if (selectedEmoji === correctEmoji) {
            setShowImage(true);
            setTimeout(() => {
                generateQuestion()
                setShowImage(false);
                setMessage("")
            }, 5000)
        } else {
            setMessage("❌ Try Again!");
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content="Help UKG kids learn left and right directions with engaging worksheets. Build spatial and directional skills through fun printable activities at Mama’s Guidance." />
                <link rel="canonical" href="https://mamasguidance.com/ukg/mathematics/pre-math-concepts/left-right" />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="contentPart additionWord pickColor matchImage match-container game-container">
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>Left and Right</h1>

                                    <p>
                                        Understanding the concepts of <strong>left</strong> and <strong>right</strong> is an important part of developing <strong>spatial and directional awareness</strong> in early learners. At Mama’s Guidance, we provide UKG children with fun, visual worksheets to help them confidently identify and use these direction-based words in real-life situations.
                                    </p>

                                    <h2>🧭 What Are Left and Right?</h2>
                                    <p>
                                        <strong>Left</strong> and <strong>Right</strong> are directional terms used to describe position and movement. Learning these helps children follow instructions, understand maps, and participate in activities such as sports and navigation.
                                    </p>

                                    <ul>
                                        <li>The apple is on the <strong>left</strong> of the plate.</li>
                                        <li>The cup is on the <strong>right</strong> of the spoon.</li>
                                    </ul>

                                    <h2>🎯 Learning Goals</h2>
                                    <ul>
                                        <li>Identify left and right on self and on objects</li>
                                        <li>Follow directions using left/right terms</li>
                                        <li>Improve body awareness and orientation</li>
                                        <li>Build spatial reasoning for everyday use</li>
                                    </ul>

                                    <h2>🖍️ Worksheet Activities Include:</h2>
                                    <ul>
                                        <li>Circle the object on the left/right</li>
                                        <li>Match items based on direction</li>
                                        <li>Color objects on the left/right of an image</li>
                                        <li>Cut-and-paste tasks for direction sorting</li>
                                        <li>Follow the arrow games to reinforce learning</li>
                                    </ul>

                                    <h2>👩‍🏫 Tips for Teaching Left and Right</h2>
                                    <ul>
                                        <li>Label your child’s left and right hands with stickers</li>
                                        <li>Play “Simon Says” with left/right actions</li>
                                        <li>Use songs and rhymes like <strong>“Put your right hand in…”</strong></li>
                                        <li>Practice while walking, drawing, or dancing</li>
                                    </ul>


                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/ukg/mathematics/pre-math-concepts/long-short"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Long - Short</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/mathematics/pre-math-concepts/heavy-light"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Heavy - Light</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/mathematics/pre-math-concepts/front-back"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Front - Back</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🌟 Mama’s Guidance Tip</h2>
                                    <p>
                                        Make learning left and right playful — dance moves, treasure hunts, and movement games are a great way to build this concept while having fun.
                                    </p>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>Left and Right</h1>

                                <p>
                                    Understanding the concepts of <strong>left</strong> and <strong>right</strong> is an important part of developing <strong>spatial and directional awareness</strong> in early learners. At Mama’s Guidance, we provide UKG children with fun, visual worksheets to help them confidently identify and use these direction-based words in real-life situations.
                                </p>

                                <h2>🧭 What Are Left and Right?</h2>
                                <p>
                                    <strong>Left</strong> and <strong>Right</strong> are directional terms used to describe position and movement. Learning these helps children follow instructions, understand maps, and participate in activities such as sports and navigation.
                                </p>

                                <ul>
                                    <li>The apple is on the <strong>left</strong> of the plate.</li>
                                    <li>The cup is on the <strong>right</strong> of the spoon.</li>
                                </ul>

                                <h2>🎯 Learning Goals</h2>
                                <ul>
                                    <li>Identify left and right on self and on objects</li>
                                    <li>Follow directions using left/right terms</li>
                                    <li>Improve body awareness and orientation</li>
                                    <li>Build spatial reasoning for everyday use</li>
                                </ul>

                                <h2>🖍️ Worksheet Activities Include:</h2>
                                <ul>
                                    <li>Circle the object on the left/right</li>
                                    <li>Match items based on direction</li>
                                    <li>Color objects on the left/right of an image</li>
                                    <li>Cut-and-paste tasks for direction sorting</li>
                                    <li>Follow the arrow games to reinforce learning</li>
                                </ul>

                                <h2>👩‍🏫 Tips for Teaching Left and Right</h2>
                                <ul>
                                    <li>Label your child’s left and right hands with stickers</li>
                                    <li>Play “Simon Says” with left/right actions</li>
                                    <li>Use songs and rhymes like <strong>“Put your right hand in…”</strong></li>
                                    <li>Practice while walking, drawing, or dancing</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/ukg/mathematics/pre-math-concepts/long-short"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Long - Short</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/mathematics/pre-math-concepts/heavy-light"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Heavy - Light</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/mathematics/pre-math-concepts/front-back"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Front - Back</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🌟 Mama’s Guidance Tip</h2>
                                <p>
                                    Make learning left and right playful — dance moves, treasure hunts, and movement games are a great way to build this concept while having fun.
                                </p>
                            </article>
                            <h2>Which one is on the {questionSide}?</h2>
                            <div style={{ display: "flex", justifyContent: "center", gap: "100px", fontSize: "50px" }}>
                                <div onClick={() => checkAnswer(leftEmoji)} style={{ cursor: "pointer" }}>
                                    {leftEmoji}
                                </div>
                                <div onClick={() => checkAnswer(rightEmoji)} style={{ cursor: "pointer" }}>
                                    {rightEmoji}
                                </div>
                            </div>
                            <p style={{ fontSize: "20px", marginTop: "20px" }}>{message}</p>
                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default LeftRightGame;
